import ClearIcon from '@mui/icons-material/Clear';
import { Autocomplete, Box, Button, Stack, SxProps, TextField, Theme } from '@mui/material';
import {
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  useGridApiContext
} from '@mui/x-data-grid-premium';
import { fieldStyles, selectMenuProps } from 'components/styles/common';
import { ToolbarFilter } from '../../../types/toolbar.types';
import { UniversalDatePicker } from '../date-picker/UniversalDatePicker';
import { UniversalDateRangePicker } from '../date-picker/UniversalDateRangePicker';

interface FilterSectionRowProps {
  /** Dynamic array containing data filter configurations */
  filters: ToolbarFilter[];
  /** System style override object leveraging Material-UI theme layout engines */
  sx?: SxProps<Theme>;
  /**
   * When enabled, automatically mounts native DataGrid layout buttons (Columns/Filters) ahead of the filter array.
   * @default true
   */
  showGridControls?: boolean;
}

export const FilterSectionRow = ({
  filters,
  sx,
  showGridControls = true
}: FilterSectionRowProps) => {
  const apiRef = useGridApiContext();

  const handleFilterChange = (filter: ToolbarFilter, newValue: any) => {
    // Forza prima l'aggiornamento dello stato locale/padre se la callback esiste
    // Questo garantisce che l'input "si setti" visivamente a schermo mostrando il testo o la data
    if (filter.onChange) {
      filter.onChange(newValue);
    }

    // Pilota la DataGrid in tempo reale
    if (apiRef?.current) {
      const targetColumnField = filter.gridField || filter.id;

      // Recupera TUTTI i filtri attualmente attivi nella griglia per preservare gli altri campi
      const currentFilters = apiRef.current.state.filter.filterModel.items || [];

      // Rimuoviamo preventivamente i vecchi filtri legati a questa specifica colonna
      // (vale sia per quando svuotiamo, sia per quando aggiorniamo, evitando duplicati)
      const updatedItems = currentFilters.filter((item) => item.field !== targetColumnField);

      // CASO 1: Gestione specifica per il RANGE DI DATE
      if (filter.type === 'dateRange') {
        if (Array.isArray(newValue)) {
          const [start, end] = newValue;
          // Usa gli operatori dedicati definiti nell'oggetto (es. ['onOrAfter', 'onOrBefore'])
          const operators = filter.gridOperators || ['onOrAfter', 'onOrBefore'];

          // Se è presente la data di inizio, aggiunge il filtro "Da" (passando una stringa pulita)
          if (start && start !== '') {
            updatedItems.push({
              id: `filter-${targetColumnField}-start`,
              field: targetColumnField,
              operator: operators[0],
              value: start // Stringa ISO pura, la DataGrid non crasherà con il .trim()
            });
          }

          // Se è presente la data di fine, aggiunge il filtro "A"
          if (end && end !== '') {
            updatedItems.push({
              id: `filter-${targetColumnField}-end`,
              field: targetColumnField,
              operator: operators[1],
              value: end // Stringa ISO pura
            });
          }
        }

        // Applica l'array di filtri aggiornato alla DataGrid
        apiRef.current.setFilterModel({ items: updatedItems });
        return; // Interrompe l'esecuzione per non entrare nella logica dei filtri singoli
      }

      // CASO 2: Gestione FILTRI SINGOLI STANDARD (Pre-esistente, ottimizzata per non sovrascrivere altre colonne)
      if (newValue === undefined || newValue === null || newValue === '') {
        // Se il valore è vuoto, applichiamo l'array dove abbiamo già rimosso la colonna corrente
        apiRef.current.setFilterModel({ items: updatedItems });
      } else {
        const targetOperator =
          filter.gridOperator || (filter.type === 'select' ? 'is' : 'contains');

        // Aggiunge il nuovo filtro singolo all'elenco dei filtri delle altre colonne
        updatedItems.push({
          id: `filter-${targetColumnField}`,
          field: targetColumnField,
          operator: targetOperator,
          value: newValue
        });

        apiRef.current.setFilterModel({ items: updatedItems });
      }
    }
  };

  const handleClearAll = () => {
    // Cicla su tutti i filtri passati e lancia la loro funzione onChange passandogli il valore vuoto
    // Questo ripulisce istantaneamente tutti gli useState nel componente padre
    filters.forEach((filter) => {
      if (filter.onChange) {
        if (filter.type === 'date') {
          filter.onChange(null);
        } else {
          filter.onChange('');
        }
      }
    });

    // Svuota il modello di filtraggio nativo della DataGrid
    if (apiRef?.current) {
      apiRef.current.setFilterModel({
        items: []
      });
    }
  };

  const hasActiveFilters = filters.some((filter) => {
    // 1. Se il valore è nativamente nullo o indefinito, il filtro non è attivo
    if (filter.value === undefined || filter.value === null) {
      return false;
    }

    // 2. CASO SPECIFICO DATE RANGE: Verifica se l'array contiene almeno un valore valido
    if (filter.type === 'dateRange' && Array.isArray(filter.value)) {
      return filter.value.some((date) => date !== null && date !== '');
    }

    // 3. CASO STANDARD: Filtri singoli, select e stringhe
    return filter.value !== '';
  });

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        flex: 1,
        flexWrap: 'wrap',
        ...sx
      }}>
      {/* DEFAULT DATAGRID MUI */}
      {showGridControls && (
        <Box
          className="flex gap-2"
          sx={{
            display: 'flex',
            alignItems: 'center',
            '& .MuiButton-root': {
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 500,
              fontSize: '13px',
              lineHeight: '1.2',
              letterSpacing: '0em',
              textTransform: 'none',
              color: '#006DB0',
              borderRadius: 2,
              height: '40px',
              px: 1.5,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: 'rgba(0, 109, 176, 0.04)',
                textDecoration: 'underline'
              }
            },
            '& .MuiButton-startIcon': {
              color: '#006DB0'
            }
          }}>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
        </Box>
      )}

      {filters.map((filter) => {
        if (filter.type === 'select') {
          const currentOption = filter.options?.find((opt) => opt.value === filter.value) || null;

          return (
            <Autocomplete
              key={filter.id}
              size="small"
              options={filter.options || []}
              getOptionLabel={(option) => String(option.label)}
              value={currentOption}
              onChange={(_, newValue) => {
                handleFilterChange(filter, newValue ? newValue.value : '');
              }}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              disablePortal={selectMenuProps.disablePortal}
              slotProps={selectMenuProps.slotProps}
              sx={{
                ...fieldStyles(filter.minWidth),
                maxWidth: 220,
                '& .MuiAutocomplete-input': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={filter.label}
                  placeholder={!filter.value ? filter.label || 'Cerca...' : ''}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    shrink: true
                  }}
                />
              )}
            />
          );
        }

        if (filter.type === 'date') {
          return (
            <UniversalDatePicker
              key={filter.id}
              name={filter.id}
              placeholder={filter.label}
              value={filter.value}
              onChange={(newValue) => handleFilterChange(filter, newValue)}
              noFuture={!!filter.noFuture}
              disabled={!!filter.disabled}
              disableOpenPicker={!!filter.disableOpenPicker}
              minWidth={filter.minWidth || 200}
              error={!!filter.error}
              helperText={filter.error || ''}
            />
          );
        }

        if (filter.type === 'dateRange') {
          return (
            <UniversalDateRangePicker
              key={filter.id}
              value={filter.value}
              onChange={(newPayload) => handleFilterChange(filter, newPayload)}
              placeholder={filter.label}
              disabled={!!filter.disabled}
              noFuture={!!filter.noFuture}
              disableOpenPicker={!!filter.disableOpenPicker}
              error={!!filter.error}
              helperText={filter.error || ''}
              minWidth={260}
            />
          );
        }

        // Default: Fallback Standard Text Input Field
        return (
          <TextField
            key={filter.id}
            size="small"
            placeholder={filter.label}
            value={filter.value || ''}
            onChange={(e) => handleFilterChange(filter, e.target.value)}
            sx={fieldStyles(filter.minWidth)}
          />
        );
      })}

      {/* BOTTONE DINAMICO DI RESET */}
      {hasActiveFilters && (
        <Button
          size="small"
          variant="text"
          color="error"
          startIcon={<ClearIcon />}
          onClick={handleClearAll}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            height: '40px',
            borderRadius: '16px !important',
            px: 1.5,
            whiteSpace: 'nowrap',
            minWidth: 'max-content',
            '&:hover': {
              backgroundColor: 'error.lighter'
            }
          }}>
          Clear
        </Button>
      )}
    </Stack>
  );
};
